import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MascotLoader from '../components/MascotLoader';
import { MASCOT_URL } from '../assets/mascot';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { FileText, Clock, CheckCircle, Users, LogOut, Search, MapPin, Calendar, Menu, Bell, Eye, Filter, UserPlus, Briefcase, Mail, PenTool, Moon, Sun, Receipt, Activity, Database, Upload, Trash2, Power, Shield, Video } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [interventions, setInterventions] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateTech, setShowCreateTech] = useState(false);
  const [techForm, setTechForm] = useState({ name: '', email: '', password: '', color: '#10B981' });
  const [creatingTech, setCreatingTech] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = useRef(null);
  const [showUsers, setShowUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const openUserManager = async () => {
    setShowUsers(true);
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/users/all`);
      setAllUsers(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte "${u.name}" (${u.email}) ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success(`Compte ${u.email} supprimé`);
      setAllUsers((prev) => prev.filter((x) => x.id !== u.id));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleUserStatus = async (u) => {
    const next = u.status === 'inactive' ? 'active' : 'inactive';
    try {
      await axios.put(`${API}/users/${u.id}/status`, { status: next });
      toast.success(next === 'active' ? 'Compte activé' : 'Compte désactivé');
      setAllUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, status: next } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const createTechnician = async () => {
    if (!techForm.name.trim() || !techForm.email.trim() || techForm.password.length < 6) {
      toast.error('Nom, email et mot de passe (6 caractères minimum) requis');
      return;
    }
    setCreatingTech(true);
    try {
      await axios.post(`${API}/users/technician`, techForm);
      toast.success('Technicien créé et activé');
      setShowCreateTech(false);
      setTechForm({ name: '', email: '', password: '', color: '#10B981' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création du technicien');
    } finally {
      setCreatingTech(false);
    }
  };

  const restoreBackup = async (f) => {
    if (!f) return;
    if (!window.confirm('Restaurer cette sauvegarde en mode Fusion ? Les données existantes seront mises à jour et les nouvelles ajoutées (rien n\'est supprimé).')) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/backup/restore`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = res.data.result || {};
      const summary = Object.entries(r).map(([k, v]) => `${k}: +${v.inserted}/~${v.updated}`).join(', ');
      toast.success(`Sauvegarde restaurée (fusion) — ${summary}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la restauration');
    } finally {
      setRestoring(false);
    }
  };

  // Notifications temps réel
  const [newNotifPulse, setNewNotifPulse] = useState(false);
  const prevNotifCount = useRef(null);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
      const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
      o2.connect(g2); g2.connect(ctx.destination); o2.type = 'sine'; o2.frequency.value = 1174;
      g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.18);
      g2.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.2);
      g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
      o2.start(ctx.currentTime + 0.18); o2.stop(ctx.currentTime + 0.56);
    } catch (e) { /* audio non dispo */ }
  };

  const pollNotifications = async () => {
    try {
      const [notifRes, unreadRes] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      const interv = notifRes.data.filter(n => n.type === 'new_intervention').length;
      if (prevNotifCount.current !== null && interv > prevNotifCount.current) {
        const diff = interv - prevNotifCount.current;
        playBeep();
        setNewNotifPulse(true);
        setTimeout(() => setNewNotifPulse(false), 8000);
        toast.success(`${diff} nouvelle${diff > 1 ? 's' : ''} intervention${diff > 1 ? 's' : ''} reçue${diff > 1 ? 's' : ''} !`, { icon: '🔔' });
      }
      prevNotifCount.current = interv;
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => {
    const id = setInterval(pollNotifications, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [intRes, statsRes, notifRes, unreadRes] = await Promise.all([
        axios.get(`${API}/interventions`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      setInterventions(intRes.data);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      prevNotifCount.current = notifRes.data.filter(n => n.type === 'new_intervention').length;
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (e) {}
  };

  const filteredInterventions = interventions.filter(i => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      i.adresse_complete?.toLowerCase().includes(searchLower) ||
      i.client_nom?.toLowerCase().includes(searchLower) ||
      i.technician_name?.toLowerCase().includes(searchLower) ||
      i.id?.toLowerCase().includes(searchLower) ||
      i.numero_dossier?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'En attente' },
      validated: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validé' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' }
    };
    const style = styles[status] || styles.submitted;
    return <Badge className={`${style.bg} ${style.text}`}>{style.label}</Badge>;
  };

  const getUrgencyDot = (color) => {
    const colors = {
      green: 'bg-green-500',
      orange: 'bg-amber-500',
      red: 'bg-red-500 animate-pulse'
    };
    return <span className={`inline-block w-3 h-3 rounded-full ${colors[color] || colors.green}`} title={
      color === 'green' ? 'Dans les temps' : color === 'orange' ? 'En retard' : 'Très en retard'
    } />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) return <MascotLoader text="Chargement du tableau de bord..." />;

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#1A1A1A]' : 'bg-[#F8F9FA]'}`} data-testid="admin-dashboard">
      <div className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar fixed top-0 left-0 w-64 h-screen z-50 transform transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <img src={LOGO} alt="KARDIF" className="h-12" />
        </div>
        
        <nav className="p-4 space-y-2">
          <a href="#" className="sidebar-link active">
            <FileText className="h-5 w-5" />
            <span>Interventions</span>
          </a>
          <a href="/planning" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/planning'); }}>
            <Calendar className="h-5 w-5" />
            <span>Planning</span>
          </a>
          <a href="/dashboard" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/dashboard'); }} data-testid="admin-tech-workspace-link">
            <Briefcase className="h-5 w-5" />
            <span>Espace terrain</span>
          </a>
          <a href="/admin/users" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/admin/users'); }}>
            <Users className="h-5 w-5" />
            <span>Utilisateurs</span>
            {stats?.pending_users > 0 && (
              <Badge className="ml-auto bg-red-500 text-white">{stats.pending_users}</Badge>
            )}
          </a>
          <a href="/arch-planner" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/arch-planner'); }}>
            <PenTool className="h-5 w-5" />
            <span>Arch-Planner</span>
          </a>
          <a href="/factures" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/factures'); }} data-testid="nav-factures">
            <Receipt className="h-5 w-5" />
            <span>Facturation</span>
          </a>
          <a href="/activite" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/activite'); }} data-testid="nav-activite">
            <Activity className="h-5 w-5" />
            <span>Activité</span>
          </a>
          <a href="/visio" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/visio'); }} data-testid="nav-visio">
            <Video className="h-5 w-5" />
            <span>Visio-Assistance</span>
          </a>
          <a href="#" className="sidebar-link" data-testid="nav-backup"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await axios.get(`${API}/backup/interventions`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `kardif_sauvegarde_${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Sauvegarde téléchargée');
              } catch (err) { toast.error('Échec de la sauvegarde'); }
            }}>
            <Database className="h-5 w-5" />
            <span>Sauvegarde</span>
          </a>
          <a href="#" className="sidebar-link" data-testid="nav-backup-xlsx"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const res = await axios.get(`${API}/backup/interventions?format=xlsx`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `kardif_sauvegarde_${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Sauvegarde Excel téléchargée');
              } catch (err) { toast.error('Échec de la sauvegarde Excel'); }
            }}>
            <Database className="h-5 w-5" />
            <span>Sauvegarde Excel</span>
          </a>
          <a href="#" className="sidebar-link" data-testid="nav-backup-restore"
            onClick={(e) => { e.preventDefault(); if (!restoring) restoreInputRef.current?.click(); }}>
            <Upload className="h-5 w-5" />
            <span>{restoring ? 'Restauration...' : 'Importer une sauvegarde'}</span>
          </a>
          <a href="#" className="sidebar-link" data-testid="nav-user-management"
            onClick={(e) => { e.preventDefault(); openUserManager(); }}>
            <Shield className="h-5 w-5" />
            <span>Gestion des utilisateurs</span>
          </a>
          <input ref={restoreInputRef} type="file" accept="application/json,.json" className="hidden"
            data-testid="backup-restore-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; restoreBackup(f); }} />
          <a 
            href={`https://mail.google.com/mail/u/?authuser=${user?.email || ''}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="sidebar-link"
          >
            <Mail className="h-5 w-5" />
            <span>Ma boîte mail</span>
          </a>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-[#6B7280]">Administrateur</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={() => { logout(); nav('/login'); }}>
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64">
        <header className="md:hidden flex items-center justify-between p-4 bg-white border-b">
          <button onClick={() => setSidebarOpen(true)}><Menu className="h-6 w-6" /></button>
          <img src="/kardif-eye.png" alt="KARDIF" className="h-9 w-9 object-contain" />
          <div className="relative">
            <Bell className="h-6 w-6" />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </div>
        </header>

        <div className="p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <img src={MASCOT_URL} alt="KARDIF" className="hidden sm:block h-14 w-auto drop-shadow mascot-live" data-testid="header-mascot" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Tableau de bord
                </h1>
                <p className="text-[#6B7280] mt-1">Gestion des comptes rendus</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleDarkMode}
                title={darkMode ? 'Mode clair' : 'Mode sombre'}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              
              <Button 
                onClick={() => nav('/intervention/new')} 
                className="bg-[#8B7355] hover:bg-[#7A6548] text-white"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Nouvelle intervention
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className={`relative ${newNotifPulse ? 'ring-2 ring-red-400 animate-bounce' : ''}`} data-testid="admin-notif-button">
                    {newNotifPulse && (
                      <span className="absolute -top-1.5 -left-1.5 flex h-3.5 w-3.5" data-testid="admin-notif-live-pulse">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500" />
                      </span>
                    )}
                    <Bell className={`h-4 w-4 mr-2 ${newNotifPulse ? 'text-red-500' : ''}`} />
                    Notifications
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="admin-notif-count">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-white">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-[#6B7280]">Aucune notification</div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <DropdownMenuItem
                      key={n.id}
                      className={`p-3 cursor-pointer ${!n.read ? 'bg-amber-50' : ''}`}
                      onClick={() => {
                        markAsRead(n.id);
                        if (n.intervention_id) nav(`/intervention/${n.intervention_id}`);
                        if (n.type === 'new_registration') nav('/admin/users');
                      }}
                    >
                      <div>
                        <p className="text-sm">{n.message}</p>
                        <p className="text-xs text-[#6B7280] mt-1">{formatDate(n.created_at)}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>

          {/* Stats détaillées */}
          {stats && (
            <div className="space-y-6 mb-8">
              {/* Ligne 1: Stats principales */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="stat-card bg-gradient-to-br from-[#8B7355]/10 to-[#8B7355]/5 border-[#8B7355]/20">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#8B7355]">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-[#1A1A1A]">{stats.total_interventions}</p>
                      <p className="stat-label text-xs text-[#6B7280]">Total Interventions</p>
                    </div>
                  </div>
                </Card>
                <Card className="stat-card bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-amber-500">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-amber-700">{stats.pending_review}</p>
                      <p className="stat-label text-xs text-amber-600">En attente</p>
                    </div>
                  </div>
                </Card>
                <Card className="stat-card bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-green-500">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-green-700">{stats.validated}</p>
                      <p className="stat-label text-xs text-green-600">Validées</p>
                    </div>
                  </div>
                </Card>
                <Card className="stat-card bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-green-500">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-green-700">{stats.finalized ?? stats.validated}</p>
                      <p className="stat-label text-xs text-green-600">Dossiers finalisés</p>
                    </div>
                  </div>
                </Card>
                <Card className="stat-card bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200" data-testid="stat-in-progress">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-orange-500">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-orange-700">{stats.in_progress ?? stats.pending_review}</p>
                      <p className="stat-label text-xs text-orange-600">Traitement en cours</p>
                    </div>
                  </div>
                </Card>
                <Card className="stat-card bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-blue-500">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="stat-value text-2xl font-bold text-blue-700">{stats.technicians}</p>
                      <p className="stat-label text-xs text-blue-600">Techniciens</p>
                    </div>
                  </div>
                </Card>
                {stats.pending_users > 0 && (
                  <Card className="stat-card bg-gradient-to-br from-red-50 to-red-100/50 border-red-300 cursor-pointer animate-pulse" onClick={() => nav('/admin/users')}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-red-500">
                        <UserPlus className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="stat-value text-2xl font-bold text-red-700">{stats.pending_users}</p>
                        <p className="stat-label text-xs text-red-600">À approuver</p>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
              
              {/* Ligne 2: Stats détaillées et graphiques */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Activité récente */}
                <Card className="p-4 border-[#8B7355]/20">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[#8B7355]" />
                    Activité récente
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-[#6B7280]">Aujourd'hui</span>
                      <span className="font-bold text-[#8B7355]">
                        {interventions.filter(i => {
                          const today = new Date();
                          const d = new Date(i.created_at);
                          return d.toDateString() === today.toDateString();
                        }).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-[#6B7280]">Cette semaine</span>
                      <span className="font-bold text-[#8B7355]">
                        {interventions.filter(i => {
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return new Date(i.created_at) >= weekAgo;
                        }).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-[#6B7280]">Ce mois</span>
                      <span className="font-bold text-[#8B7355]">
                        {interventions.filter(i => {
                          const monthAgo = new Date();
                          monthAgo.setMonth(monthAgo.getMonth() - 1);
                          return new Date(i.created_at) >= monthAgo;
                        }).length}
                      </span>
                    </div>
                  </div>
                </Card>
                
                {/* Répartition par statut */}
                <Card className="p-4 border-[#8B7355]/20">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-[#8B7355]" />
                    Répartition par statut
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${stats.total_interventions > 0 ? (stats.validated / stats.total_interventions * 100) : 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-20 text-right text-green-600">
                        {stats.total_interventions > 0 ? Math.round(stats.validated / stats.total_interventions * 100) : 0}% Validé
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${stats.total_interventions > 0 ? (stats.pending_review / stats.total_interventions * 100) : 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-20 text-right text-amber-600">
                        {stats.total_interventions > 0 ? Math.round(stats.pending_review / stats.total_interventions * 100) : 0}% Attente
                      </span>
                    </div>
                  </div>
                </Card>
                
                {/* Actions rapides */}
                <Card className="p-4 border-[#8B7355]/20">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#8B7355]" />
                    Actions rapides
                  </h3>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                      onClick={() => setShowCreateTech(true)}
                      data-testid="create-technician-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-2 text-[#8B7355]" />
                      Créer un technicien
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                      onClick={() => nav('/planning')}
                    >
                      <Calendar className="h-4 w-4 mr-2 text-[#8B7355]" />
                      Voir le planning
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                      onClick={() => nav('/admin/users')}
                    >
                      <Users className="h-4 w-4 mr-2 text-[#8B7355]" />
                      Gérer les utilisateurs
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                      onClick={() => nav('/statistics')}
                    >
                      <Eye className="h-4 w-4 mr-2 text-[#8B7355]" />
                      Voir les statistiques
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <Input
                placeholder="Rechercher par adresse, client, technicien, n° dossier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg">
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="submitted">En attente</SelectItem>
                <SelectItem value="validated">Validé</SelectItem>
                <SelectItem value="rejected">Rejeté</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : filteredInterventions.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="h-12 w-12 text-[#D4C5A9] mx-auto mb-4" />
                <p className="text-[#6B7280]">Aucune intervention</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Adresse</th>
                      <th>Technicien</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInterventions.map((i) => (
                      <tr key={i.id}>
                        <td className="w-8">{getUrgencyDot(i.urgency_color)}</td>
                        <td>
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-[#8B7355] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium">{i.adresse_complete}</p>
                              <p className="text-sm text-[#6B7280]">{i.type_batiment}</p>
                              <p className="text-xs text-[#9CA3AF]">N° {i.numero_dossier || i.id?.substring(0, 8)}</p>
                              {i.franchise && (
                                <Badge className="mt-1 bg-amber-100 text-amber-800 text-xs">Franchise</Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{i.technician_name}</td>
                        <td className="text-[#6B7280]">{i.type_intervention?.join(', ')}</td>
                        <td>
                          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                            <Calendar className="h-4 w-4" />
                            {formatDate(i.created_at)}
                          </div>
                        </td>
                        <td>{getStatusBadge(i.status)}</td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => nav(`/intervention/${i.id}`)}
                            className="text-[#8B7355]"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </main>

      <Dialog open={showCreateTech} onOpenChange={setShowCreateTech}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="create-technician-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#8B7355]"><UserPlus className="h-5 w-5" />Créer un technicien</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="tech-name">Nom complet</Label>
              <Input id="tech-name" data-testid="tech-name-input" value={techForm.name}
                onChange={(e) => setTechForm({ ...techForm, name: e.target.value })} placeholder="Jean Dupont" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tech-email">Email</Label>
              <Input id="tech-email" type="email" data-testid="tech-email-input" value={techForm.email}
                onChange={(e) => setTechForm({ ...techForm, email: e.target.value })} placeholder="j.dupont@kardifinvestigation.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tech-password">Mot de passe (6 caractères min.)</Label>
              <Input id="tech-password" type="text" data-testid="tech-password-input" value={techForm.password}
                onChange={(e) => setTechForm({ ...techForm, password: e.target.value })} placeholder="••••••" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tech-color">Couleur planning</Label>
              <div className="flex items-center gap-3">
                <input id="tech-color" type="color" data-testid="tech-color-input" value={techForm.color}
                  onChange={(e) => setTechForm({ ...techForm, color: e.target.value })}
                  className="h-10 w-16 rounded border border-[#E5E0D8] cursor-pointer" />
                <span className="text-sm text-[#6B7280]">{techForm.color}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTech(false)}>Annuler</Button>
            <Button onClick={createTechnician} disabled={creatingTech}
              className="bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="tech-submit-btn">
              {creatingTech ? 'Création...' : 'Créer le technicien'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUsers} onOpenChange={setShowUsers}>
        <DialogContent className="bg-white sm:max-w-2xl" data-testid="user-management-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#8B7355]"><Shield className="h-5 w-5" />Gestion des utilisateurs</DialogTitle>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            {loadingUsers ? (
              <p className="text-sm text-[#6B7280] py-6 text-center">Chargement...</p>
            ) : (
              <ul className="space-y-2" data-testid="user-list">
                {allUsers.map((u) => {
                  const isSelf = u.id === user?.id;
                  const roleLabel = u.role === 'admin' ? 'Administrateur' : u.role === 'secretary' ? 'Secrétaire' : 'Technicien';
                  const roleColor = u.role === 'admin' ? 'bg-purple-100 text-purple-700' : u.role === 'secretary' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700';
                  return (
                    <li key={u.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E5E0D8] bg-[#F8F5F0]" data-testid={`user-row-${u.email}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1A1A1A] truncate">{u.name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${roleColor}`}>{roleLabel}</span>
                          {u.status === 'inactive' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">Désactivé</span>}
                          {isSelf && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B7355]/15 text-[#8B7355] font-medium">Vous</span>}
                        </div>
                        <p className="text-xs text-[#6B7280] truncate">{u.email}</p>
                      </div>
                      {!isSelf && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => toggleUserStatus(u)} data-testid={`toggle-status-${u.email}`}
                            className="border-[#8B7355]/30 hover:bg-[#8B7355]/10">
                            <Power className="h-3.5 w-3.5 mr-1" />{u.status === 'inactive' ? 'Activer' : 'Désactiver'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.email}`}
                            className="border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="text-xs text-[#6B7280]">Votre propre compte ne peut pas être supprimé ni désactivé. Le dernier administrateur est protégé.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
